import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import {
  type BrandbEntry,
  type BrandEdge,
  type BrandEdgeRole,
  CompanyId,
  type ComparisonEdge,
  type GroupSuggestion,
  groupKeyFromName,
  type LeadCompany,
  type LeadCompanyDraft,
  type LeadStatus,
  nameNorm,
  type PersonEdge,
} from './lead-company';
import {
  type CountryBucket,
  effectiveStatuses,
  type LeadQuery,
  NAMED_COUNTRIES,
  PAGE_SIZE,
  THRESHOLD_COLUMNS,
  THRESHOLD_KEYS,
} from './lead-company-query';
import {
  type AposellJobType,
  type ComparisonJobRequest,
  type JobCounts,
  type JobLogEntry,
  type JobStatus,
  LeadCompanyRepository,
  type LeadEditPatch,
  type LeadPage,
  type SampleReportRequest,
} from './lead-company-repository';

const TABLE = 'portal_aposell_company';
const BRANDB_TABLE = 'brandb';
const REFRESH_COMPARISONS_RPC = 'aposell_refresh_comparisons';
const JOBS_TABLE = 'portal_report_jobs';
const COMPARISON_JOB_TYPE = 'aposell_comparison_generate';
const SAMPLE_REPORT_JOB_TYPE = 'sample_report_generate';

const JOB_LOG_COLUMNS = 'id, status, created_at, error, params, result_jsonb';

/** How far back a finished job still counts as part of the current batch. */
const JOB_WINDOW_MS = 24 * 60 * 60 * 1000;

/** PostgREST's silent cap on an unpaginated read (.claude/rules/data-access.md). */
const POSTGREST_PAGE_CAP = 1000;

/** Postgres `unique_violation`, the one insert failure worth a second try with a fresh id. */
const UNIQUE_VIOLATION = '23505';

/** The existing ids are ten lowercase hex characters (user ruling 2026-09-16). */
const COMPANY_ID_LENGTH = 10;

/**
 * A fresh `company_id` in the format the table already holds. The table has no default, so
 * the frontend mints it; a collision is caught by the primary key and retried once.
 */
function newCompanyId(): string {
  const bytes = new Uint8Array(COMPANY_ID_LENGTH / 2);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * The row as it stands in prod. Hand-written rather than taken from `db.types.ts`, which does
 * not carry this table: regenerating the whole generated file for one table was declined
 * (user ruling 2026-09-07). Every column is nullable except the primary
 * key, because the generated file is what would otherwise pin nullability and we do not have
 * it here. Over-nullable forces handling; under-nullable would let a null through untyped.
 */
interface AposellCompanyRow {
  company_id: string;
  name: string | null;
  name_variations: string | null;
  name_norm: string | null;
  group_key: string | null;
  group_manual: string | null;
  country: string | null;
  domain: string | null;
  org_vat: string | null;
  org_nr: string | null;
  status: string | null;
  clickup_status: string | null;
  portal_org_id: string | null;
  owner: string | null;
  last_contacted_person: string | null;
  next_action: string | null;
  language: string | null;
  linkedin_url: string | null;
  revenue_raw: string | null;
  main_categories: string | null;
  roles: string | null;
  memberships: string | null;
  notes: string | null;
  sources: string | null;
  review_flag: string | null;
  signal_period: string | null;
  do_not_contact: boolean | null;
  last_contacted: string | null;
  next_action_date: string | null;
  founded_year: number | null;
  revenue_sek: number | null;
  employees: number | null;
  portal_products_n: number | null;
  brands_in_portal_n: number | null;
  products_nonrx_n: number | null;
  priority: number | null;
  signal_revenue_online: number | null;
  signal_revenue_retail: number | null;
  signal_vendors_online: number | null;
  signal_vendors_retail: number | null;
  signal_ratio: number | null;
  has_person_email: boolean | null;
  comparisons_n: number | null;
  top_comparison_pct: number | null;
  persons: unknown;
  brands: unknown;
  comparisons: unknown;
}

/** Every column the UI reads. `search_text` is a query surface only, never fetched. */
const COLUMNS = [
  'company_id',
  'name',
  'name_variations',
  'name_norm',
  'group_key',
  'group_manual',
  'country',
  'domain',
  'org_vat',
  'org_nr',
  'status',
  'clickup_status',
  'portal_org_id',
  'owner',
  'last_contacted_person',
  'next_action',
  'language',
  'linkedin_url',
  'revenue_raw',
  'main_categories',
  'roles',
  'memberships',
  'notes',
  'sources',
  'review_flag',
  'signal_period',
  'do_not_contact',
  'last_contacted',
  'next_action_date',
  'founded_year',
  'revenue_sek',
  'employees',
  'portal_products_n',
  'brands_in_portal_n',
  'products_nonrx_n',
  'priority',
  'signal_revenue_online',
  'signal_revenue_retail',
  'signal_vendors_online',
  'signal_vendors_retail',
  'signal_ratio',
  'has_person_email',
  'comparisons_n',
  'top_comparison_pct',
  'persons',
  'brands',
  'comparisons',
].join(', ');

/**
 * Wraps a filter value so PostgREST reads it as one literal. Company names carry commas,
 * parentheses and periods, all of which are PostgREST grammar (.claude/rules/data-access.md,
 * "Escape special characters in PostgREST filter values"). `%` and `_` are left alone: in a
 * search box a typed wildcard is harmless.
 */
function quoted(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

function inList(values: readonly string[]): string {
  return `(${values.map(quoted).join(',')})`;
}

/**
 * A typed term with the `ilike` metacharacters removed, or '' when nothing is left to match on.
 * Commas and parentheses stay: `.ilike()` encodes its value, so they reach Postgres as literals.
 */
function searchTerm(raw: string): string {
  return raw
    .trim()
    .replace(/[\\%*]/g, '')
    .trim();
}

/** The country spellings one bucket claims, including the guessed trailing-question-mark form. */
function spellingsOf(country: string): readonly string[] {
  return [country, `${country}?`];
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0
  );
}

function asRole(value: unknown): BrandEdgeRole {
  return value === 'owner' || value === 'distributor' ? value : 'unknown';
}

function objectEntriesOf(value: unknown): readonly Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null
  );
}

/** Jsonb arrives untyped, so both lists parse defensively: one bad entry must not lose the row. */
function parsePersons(value: unknown): readonly PersonEdge[] {
  return objectEntriesOf(value).map((entry) => ({
    name: asString(entry['name']),
    title: asString(entry['title']),
    email: asString(entry['email']),
    linkedin: asString(entry['linkedin']),
    source: asString(entry['source']),
  }));
}

function parseBrands(value: unknown): readonly BrandEdge[] {
  return objectEntriesOf(value).map((entry) => ({
    raw: asString(entry['raw']),
    portalName: asString(entry['portal_name']),
    role: asRole(entry['role']),
    source: asString(entry['source']),
    brandname: asString(entry['brandname']),
    brandalias: asString(entry['brandalias']),
    brandurl: asString(entry['brandurl']),
    products: asString(entry['products']),
    cats: asString(entry['cats']),
  }));
}

/** Order is the function's own (company_products_n descending), so it is preserved as read. */
function parseComparisons(value: unknown): readonly ComparisonEdge[] {
  return (
    objectEntriesOf(value)
      .map((entry) => ({
        id: asString(entry['id']),
        name: asString(entry['name']),
        productsTotal: asNumber(entry['products_total']),
        companyProductsN: asNumber(entry['company_products_n']),
        companyProductsPct: asNumber(entry['company_products_pct']),
        companyBrands: asStringList(entry['company_brands']),
      }))
      // An edge with no id cannot be linked to, which is the whole point of the row.
      .filter((edge): edge is ComparisonEdge => edge.id !== null)
  );
}

function toLeadCompany(row: AposellCompanyRow): LeadCompany {
  return {
    companyId: CompanyId(row.company_id),
    name: row.name,
    nameVariations: row.name_variations,
    nameNorm: row.name_norm,
    groupKey: row.group_key,
    groupManual: row.group_manual,
    country: row.country,
    domain: row.domain,
    orgVat: row.org_vat,
    orgNr: row.org_nr,
    status: row.status as LeadStatus | null,
    clickupStatus: row.clickup_status,
    portalOrgId: row.portal_org_id,
    owner: row.owner,
    lastContactedPerson: row.last_contacted_person,
    nextAction: row.next_action,
    language: row.language,
    linkedinUrl: row.linkedin_url,
    revenueRaw: row.revenue_raw,
    mainCategories: row.main_categories,
    roles: row.roles,
    memberships: row.memberships,
    notes: row.notes,
    sources: row.sources,
    reviewFlag: row.review_flag,
    signalPeriod: row.signal_period,
    doNotContact: row.do_not_contact,
    lastContacted: row.last_contacted,
    nextActionDate: row.next_action_date,
    foundedYear: row.founded_year,
    revenueSek: row.revenue_sek,
    employees: row.employees,
    portalProductsN: row.portal_products_n,
    brandsInPortalN: row.brands_in_portal_n,
    productsNonrxN: row.products_nonrx_n,
    priority: row.priority,
    signalRevenueOnline: row.signal_revenue_online,
    signalRevenueRetail: row.signal_revenue_retail,
    signalVendorsOnline: row.signal_vendors_online,
    signalVendorsRetail: row.signal_vendors_retail,
    signalRatio: row.signal_ratio,
    hasPersonEmail: row.has_person_email,
    comparisonsN: row.comparisons_n,
    topComparisonPct: row.top_comparison_pct,
    persons: parsePersons(row.persons),
    brands: parseBrands(row.brands),
    comparisons: parseComparisons(row.comparisons),
  };
}

function jobOutcome(result: unknown, status: string): string | null {
  if (status !== 'succeeded' || typeof result !== 'object' || result === null) return null;
  const row = result as Record<string, unknown>;
  const coverage = asNumber(row['company_products_pct']);
  const comparison = asString(row['comparison_name']);
  if (comparison) {
    return coverage === null ? comparison : `${comparison} (${Math.round(coverage)}%)`;
  }
  return asString(row['email_sent_to']);
}

function toJobLogEntry(row: unknown): JobLogEntry {
  const typed = row as {
    id: string;
    status: string;
    created_at: string;
    error: string | null;
    params: unknown;
    result_jsonb: unknown;
  };
  const params = (typed.params ?? {}) as Record<string, unknown>;
  return {
    jobId: typed.id,
    companyId: asString(params['company_id']),
    prospectName: asString(params['prospect_name']),
    status: typed.status as JobStatus,
    createdAt: typed.created_at,
    error: typed.error,
    outcome: jobOutcome(typed.result_jsonb, typed.status),
  };
}

function toPersonRow(person: PersonEdge): Record<string, unknown> {
  return {
    name: person.name,
    title: person.title,
    email: person.email,
    linkedin: person.linkedin,
    source: person.source,
  };
}

function toBrandRow(brand: BrandEdge): Record<string, unknown> {
  return {
    raw: brand.raw,
    portal_name: brand.portalName,
    role: brand.role,
    source: brand.source,
    brandname: brand.brandname,
    brandalias: brand.brandalias,
    brandurl: brand.brandurl,
    products: brand.products,
    cats: brand.cats,
  };
}

/**
 * The insert row for a draft: only the columns the form filled (user ruling 2026-09-16).
 * Every not-null column with a table default (status, do_not_contact, priority, the counters,
 * and the persons, brands, comparisons, touches and reports jsonb lists) is left out so the
 * default applies. `search_text`, `has_person_email` and `signal_ratio` are generated and
 * never written. `name_norm` and an unchosen `group_key` are derived from the name the way
 * the scripts derive them; `group_manual` marks only a key the user picked or typed.
 */
function toInsertRow(id: string, draft: LeadCompanyDraft): Record<string, unknown> {
  const row: Record<string, unknown> = {
    company_id: id,
    name: draft.name,
    country: draft.country,
    domain: draft.domain,
    name_norm: nameNorm(draft.name),
    group_key: draft.groupKey ?? groupKeyFromName(draft.name),
    sources: 'manual',
  };
  const optional: readonly [string, unknown][] = [
    ['name_variations', draft.nameVariations],
    // No group edit path exists in the frontend, so a hand-picked group is marked plainly.
    ['group_manual', draft.groupKey === null ? null : 'true'],
    ['org_nr', draft.orgNr],
    ['org_vat', draft.orgVat],
    ['founded_year', draft.foundedYear],
    ['revenue_sek', draft.revenueSek],
    ['revenue_raw', draft.revenueRaw],
    ['employees', draft.employees],
    ['linkedin_url', draft.linkedinUrl],
    ['language', draft.language],
    ['main_categories', draft.mainCategories],
    ['roles', draft.roles],
    ['memberships', draft.memberships],
    ['notes', draft.notes],
  ];
  for (const [column, value] of optional) {
    if (value !== null) row[column] = value;
  }
  return row;
}

function toRowPatch(patch: LeadEditPatch): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if ('status' in patch) row['status'] = patch.status;
  if ('owner' in patch) row['owner'] = patch.owner;
  if ('doNotContact' in patch) row['do_not_contact'] = patch.doNotContact;
  if ('nextAction' in patch) row['next_action'] = patch.nextAction;
  if ('nextActionDate' in patch) row['next_action_date'] = patch.nextActionDate;
  if ('language' in patch) row['language'] = patch.language;
  if ('notes' in patch) row['notes'] = patch.notes;
  if ('lastContacted' in patch) row['last_contacted'] = patch.lastContacted;
  if ('lastContactedPerson' in patch) row['last_contacted_person'] = patch.lastContactedPerson;
  return row;
}

/** The subset of the PostgREST builder the country predicate needs, so it can stay generic. */
interface CountryFilterable<T> {
  or(filter: string): T;
  not(column: string, operator: string, value: unknown): T;
}

@Injectable()
export class SupabaseLeadCompanyRepository extends LeadCompanyRepository {
  // SupabaseService rather than AuthStore: domain code may not import from portal/
  // outside domain/ (.claude/rules/portal-domain.md). Same client object either way.
  private readonly supabase = inject(SupabaseService);

  async page(query: LeadQuery): Promise<LeadPage> {
    const supabase = this.supabase.client;
    const from = query.page * PAGE_SIZE;

    let request = supabase.from(TABLE).select(COLUMNS, { count: 'exact' });

    // `search_text` is stored lowercased; `ilike` is case-insensitive either way.
    const search = searchTerm(query.search);
    if (search) {
      request = request.ilike('search_text', `%${search.toLowerCase()}%`);
    }

    request = request.in('status', [...effectiveStatuses(query)]);

    // The checkbox keeps its `last_contacted is null` half even once the multi-select has
    // taken over the status half (user ruling 2026-09-07).
    if (query.notContactedYet) {
      request = request.is('last_contacted', null);
    }

    request = applyCountry(request, query.country);

    if (query.hasBrandsInPortal) {
      request = request.gte('brands_in_portal_n', 1);
    }
    if (query.hasContactEmail) {
      request = request.eq('has_person_email', true);
    }
    if (!query.includeDoNotContact) {
      // A null flag is not a do-not-contact, so it must survive the exclusion.
      request = request.or('do_not_contact.is.null,do_not_contact.eq.false');
    }
    // A floor keeps the ratio sentinel (an infinite ratio clears any floor); a ceiling drops
    // it, which is equally right -- an infinite ratio is not "at most 5x".
    for (const key of THRESHOLD_KEYS) {
      const threshold = query.thresholds[key];
      if (threshold.value === null) continue;
      const column = THRESHOLD_COLUMNS[key];
      request =
        threshold.direction === 'min'
          ? request.gte(column, threshold.value)
          : request.lte(column, threshold.value);
    }
    if (query.hasComparisons === true) {
      request = request.gte('comparisons_n', 1);
    } else if (query.hasComparisons === false) {
      request = request.eq('comparisons_n', 0);
    }
    if (query.belowTopComparisonPct !== null) {
      // A company in no comparison has no coverage figure at all, and it is the most
      // under-served of the lot -- a plain `lt` would drop exactly the rows being hunted.
      request = request.or(
        `top_comparison_pct.lt.${query.belowTopComparisonPct},top_comparison_pct.is.null`
      );
    }

    const { data, error, count } = await request
      .order(query.sortKey, { ascending: query.sortAscending, nullsFirst: false })
      // Without a stable tiebreaker, ties reshuffle between pages and rows repeat or vanish.
      .order('company_id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      throw new Error(`[LeadCompanyRepository] page read of ${TABLE} failed: ${error.message}`);
    }

    return {
      rows: ((data ?? []) as unknown as AposellCompanyRow[]).map(toLeadCompany),
      total: count ?? 0,
    };
  }

  async siblings(groupKeys: readonly string[]): Promise<readonly LeadCompany[]> {
    if (groupKeys.length === 0) return [];
    const supabase = this.supabase.client;
    const rows: LeadCompany[] = [];
    let offset = 0;

    // A page's groups can hold more rows than PostgREST returns unasked, so page until short.
    for (;;) {
      const { data, error } = await supabase
        .from(TABLE)
        .select(COLUMNS)
        .in('group_key', [...groupKeys])
        .order('company_id', { ascending: true })
        .range(offset, offset + POSTGREST_PAGE_CAP - 1);

      if (error) {
        throw new Error(
          `[LeadCompanyRepository] sibling read of ${TABLE} failed: ${error.message}`
        );
      }

      const page = (data ?? []) as unknown as AposellCompanyRow[];
      rows.push(...page.map(toLeadCompany));
      if (page.length < POSTGREST_PAGE_CAP) return rows;
      offset += POSTGREST_PAGE_CAP;
    }
  }

  private async countJobs(
    jobType: AposellJobType,
    narrow: (request: ReturnType<typeof this.jobQuery>) => ReturnType<typeof this.jobQuery>
  ): Promise<number> {
    const { count, error } = await narrow(this.jobQuery(jobType));
    if (error) {
      throw new Error(
        `[LeadCompanyRepository] job count on ${JOBS_TABLE} failed: ${error.message}`
      );
    }
    return count ?? 0;
  }

  private jobQuery(jobType: AposellJobType) {
    return this.supabase.client
      .from(JOBS_TABLE)
      .select('id', { count: 'exact', head: true })
      .eq('params->>job_type', jobType);
  }

  async update(id: CompanyId, patch: LeadEditPatch): Promise<LeadCompany> {
    const supabase = this.supabase.client;
    const { data, error } = await supabase
      .from(TABLE)
      .update(toRowPatch(patch))
      .eq('company_id', id)
      .select(COLUMNS)
      .single();

    if (error) {
      throw new Error(`[LeadCompanyRepository] update of ${TABLE} failed: ${error.message}`);
    }
    return toLeadCompany(data as unknown as AposellCompanyRow);
  }

  async queueComparisonJobs(requests: readonly ComparisonJobRequest[]): Promise<number> {
    if (requests.length === 0) return 0;

    const userId = await this.currentUserId();

    const rows = requests.map((request) => ({
      status: 'queued',
      created_by: userId,
      params: {
        job_type: COMPARISON_JOB_TYPE,
        company_id: request.companyId,
        ...(request.operatorHint ? { operator_hint: request.operatorHint } : {}),
      },
    }));

    const { data, error } = await this.supabase.client.from(JOBS_TABLE).insert(rows).select('id');
    if (error) {
      throw new Error(
        `[LeadCompanyRepository] job queue on ${JOBS_TABLE} failed: ${error.message}`
      );
    }
    return (data ?? []).length;
  }

  async queueSampleReport(request: SampleReportRequest): Promise<string> {
    const userId = await this.currentUserId();
    const { data, error } = await this.supabase.client
      .from(JOBS_TABLE)
      .insert({
        status: 'queued',
        created_by: userId,
        params: {
          job_type: SAMPLE_REPORT_JOB_TYPE,
          pseudobrand_id: request.comparisonId,
          prospect_name: request.prospectName,
          email_to: request.emailTo,
          ...(request.mapFocusBrand ? { map_focus_brand: request.mapFocusBrand } : {}),
        },
      })
      .select('id')
      .single();

    if (error || !data) {
      throw new Error(
        `[LeadCompanyRepository] report queue on ${JOBS_TABLE} failed: ${error?.message ?? 'no row returned'}`
      );
    }
    return (data as { id: string }).id;
  }

  // RLS rejects any created_by but the caller's own uid, and the session is the only
  // place that value may come from.
  private async currentUserId(): Promise<string> {
    const {
      data: { session },
    } = await this.supabase.client.auth.getSession();
    const userId = session?.user.id;
    if (!userId) {
      throw new Error('[LeadCompanyRepository] no signed-in session; cannot queue jobs');
    }
    return userId;
  }

  async recentJobs(jobType: AposellJobType, limit: number): Promise<readonly JobLogEntry[]> {
    const { data, error } = await this.supabase.client
      .from(JOBS_TABLE)
      .select(JOB_LOG_COLUMNS)
      .eq('params->>job_type', jobType)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`[LeadCompanyRepository] job log on ${JOBS_TABLE} failed: ${error.message}`);
    }
    return (data ?? []).map(toJobLogEntry);
  }

  async latestComparisonJobs(
    companyIds: readonly string[]
  ): Promise<ReadonlyMap<string, JobLogEntry>> {
    if (companyIds.length === 0) return new Map();

    const { data, error } = await this.supabase.client
      .from(JOBS_TABLE)
      .select(JOB_LOG_COLUMNS)
      .eq('params->>job_type', COMPARISON_JOB_TYPE)
      .in('params->>company_id', [...companyIds])
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(
        `[LeadCompanyRepository] job lookup on ${JOBS_TABLE} failed: ${error.message}`
      );
    }

    // Newest first, so the first row seen for a company is the one that counts.
    const latest = new Map<string, JobLogEntry>();
    for (const row of data ?? []) {
      const entry = toJobLogEntry(row);
      if (entry.companyId && !latest.has(entry.companyId)) latest.set(entry.companyId, entry);
    }
    return latest;
  }

  async companyNames(companyIds: readonly string[]): Promise<ReadonlyMap<string, string>> {
    if (companyIds.length === 0) return new Map();

    const { data, error } = await this.supabase.client
      .from(TABLE)
      .select('company_id, name')
      .in('company_id', [...companyIds]);

    if (error) {
      throw new Error(`[LeadCompanyRepository] name lookup on ${TABLE} failed: ${error.message}`);
    }
    return new Map(
      (data ?? []).map((row) => {
        const typed = row as { company_id: string; name: string | null };
        return [typed.company_id, typed.name ?? typed.company_id];
      })
    );
  }

  /** `head: true` answers from the Content-Range header alone, so a poll moves no rows. */
  async jobCounts(jobType: AposellJobType): Promise<JobCounts> {
    const since = new Date(Date.now() - JOB_WINDOW_MS).toISOString();
    const [pending, done, failed] = await Promise.all([
      this.countJobs(jobType, (request) => request.in('status', ['queued', 'running'])),
      this.countJobs(jobType, (request) =>
        request.eq('status', 'succeeded').gte('updated_at', since)
      ),
      this.countJobs(jobType, (request) => request.eq('status', 'failed').gte('updated_at', since)),
    ]);
    return { pending, done, failed };
  }

  async replacePersons(id: CompanyId, persons: readonly PersonEdge[]): Promise<LeadCompany> {
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .update({ persons: persons.map(toPersonRow) })
      .eq('company_id', id)
      .select(COLUMNS)
      .single();

    if (error) {
      throw new Error(`[LeadCompanyRepository] persons write on ${TABLE} failed: ${error.message}`);
    }
    return toLeadCompany(data as unknown as AposellCompanyRow);
  }

  async replaceBrands(id: CompanyId, brands: readonly BrandEdge[]): Promise<LeadCompany> {
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .update({ brands: brands.map(toBrandRow) })
      .eq('company_id', id)
      .select(COLUMNS)
      .single();

    if (error) {
      throw new Error(`[LeadCompanyRepository] brands write on ${TABLE} failed: ${error.message}`);
    }
    return toLeadCompany(data as unknown as AposellCompanyRow);
  }

  async create(draft: LeadCompanyDraft): Promise<LeadCompany> {
    const first = await this.insertCompany(newCompanyId(), draft);
    if (first.row) return first.row;
    // One collision on a ten-hex-character id is bad luck; a second is a real fault.
    if (first.error.code !== UNIQUE_VIOLATION) {
      throw new Error(
        `[LeadCompanyRepository] insert into ${TABLE} failed: ${first.error.message}`
      );
    }
    const second = await this.insertCompany(newCompanyId(), draft);
    if (second.row) return second.row;
    throw new Error(
      `[LeadCompanyRepository] insert into ${TABLE} failed twice: ${second.error.message}`
    );
  }

  private async insertCompany(
    id: string,
    draft: LeadCompanyDraft
  ): Promise<
    { row: LeadCompany; error: null } | { row: null; error: { code: string; message: string } }
  > {
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .insert(toInsertRow(id, draft))
      .select(COLUMNS)
      .single();
    if (error) return { row: null, error: { code: error.code ?? '', message: error.message } };
    return { row: toLeadCompany(data as unknown as AposellCompanyRow), error: null };
  }

  async brandbLookup(slug: string): Promise<BrandbEntry | null> {
    if (!slug) return null;
    const { data, error } = await this.supabase.client
      .from(BRANDB_TABLE)
      .select('brandname, brandalias, brandurl')
      .eq('brandname', slug)
      .maybeSingle();

    if (error) {
      throw new Error(`[LeadCompanyRepository] lookup on ${BRANDB_TABLE} failed: ${error.message}`);
    }
    const typed = data as {
      brandname: string | null;
      brandalias: string | null;
      brandurl: string | null;
    } | null;
    const brandname = asString(typed?.brandname);
    if (brandname === null) return null;
    return {
      brandname,
      brandalias: asString(typed?.brandalias),
      brandurl: asString(typed?.brandurl),
    };
  }

  async groupSuggestions(term: string, limit: number): Promise<readonly GroupSuggestion[]> {
    const needle = searchTerm(term);
    if (!needle) return [];
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .select('company_id, name, group_key')
      .ilike('name', `%${needle}%`)
      .not('group_key', 'is', null)
      .order('name', { ascending: true })
      .limit(limit);

    if (error) {
      throw new Error(`[LeadCompanyRepository] group lookup on ${TABLE} failed: ${error.message}`);
    }
    const suggestions: GroupSuggestion[] = [];
    for (const raw of data ?? []) {
      const typed = raw as { company_id: string; name: string | null; group_key: string | null };
      const groupKey = asString(typed.group_key);
      if (groupKey === null) continue;
      suggestions.push({ companyId: CompanyId(typed.company_id), name: typed.name, groupKey });
    }
    return suggestions;
  }

  async refreshComparisons(): Promise<number> {
    const supabase = this.supabase.client;
    const { data, error } = await supabase.rpc(REFRESH_COMPARISONS_RPC);

    if (error) {
      throw new Error(
        `[LeadCompanyRepository] ${REFRESH_COMPARISONS_RPC} failed: ${error.message}`
      );
    }
    // The function returns one integer; anything else means the contract moved under us.
    if (typeof data !== 'number') {
      throw new Error(
        `[LeadCompanyRepository] ${REFRESH_COMPARISONS_RPC} returned ${typeof data}, expected a number`
      );
    }
    return data;
  }
}

function applyCountry<T extends CountryFilterable<T>>(request: T, bucket: CountryBucket): T {
  if (bucket === 'ALL') return request;
  if (bucket === 'OTHER') {
    const claimed = NAMED_COUNTRIES.flatMap((country) => [...spellingsOf(country)]);
    return request.not('country', 'in', inList([...claimed, ''])).not('country', 'is', null);
  }
  if (bucket === 'SE') {
    // Sweden also claims the rows with no country at all (user ruling 2026-09-07).
    return request.or(`country.in.${inList([...spellingsOf('SE'), ''])},country.is.null`);
  }
  return request.or(`country.in.${inList(spellingsOf(bucket))}`);
}
